import { IFile, SVGFC } from '@repo/toolkit/types'
import ActionButton, { ActionButtonVariant } from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import { DeleteIcon, UploadIcon } from '@repo/ui/icons'
import { ReactNode } from 'react'

import ActionTableWrapper from './action-table-wrapper'
import DisplayImageInfo from './display-image-info'

type Props = {
  title: string
  files: IFile[]
  getLabel: (file: IFile) => string
  getInfoSection?: (file: IFile) => ReactNode
  onClick: () => void
  onDelete: (file: IFile) => void
  uploadLabel?: string
  emptyState?: ReactNode
  customActions?: ReactNode[]
  imageSizeVariant?: 'small' | 'large'
  itemClassName?: string
  actionVariant?: ActionButtonVariant
  actionIcon?: SVGFC
  hideActions?: boolean
}

/**
 * A reusable component for displaying a list of media attachments with actions.
 * Can be used for displaying various types of media files like documents, images, etc.
 */
const MediaAttachmentList = ({
  title,
  files = [],
  getLabel,
  getInfoSection,
  onClick,
  onDelete,
  uploadLabel = 'Upload document',
  emptyState,
  customActions = [],
  imageSizeVariant = 'large',
  itemClassName = 'flex flex-col gap-3',
  actionVariant = 'danger',
  actionIcon = DeleteIcon,
  hideActions = false
}: Props) => {
  const defaultUploadButton = (
    <Button
      key="upload-button"
      type="button"
      icon={UploadIcon}
      label={uploadLabel}
      variant="secondary"
      onClick={onClick}
    />
  )

  // Combine default upload button with any custom actions
  const actions = [defaultUploadButton, ...customActions]

  return (
    <ActionTableWrapper title={title} actions={actions}>
      {files.length > 0 ? (
        <div className={itemClassName}>
          {files.map((file, index) => (
            <DisplayImageInfo
              key={index}
              file={file}
              imageSizeVariant={imageSizeVariant}
              informationSection={getInfoSection ? getInfoSection(file) : null}
              actions={
                !hideActions && (
                  <ActionButton
                    type="button"
                    variant={actionVariant}
                    icon={actionIcon}
                    onClick={() => onDelete(file)}
                  />
                )
              }
              label={getLabel(file)}
            />
          ))}
        </div>
      ) : (
        emptyState || null
      )}
    </ActionTableWrapper>
  )
}

export default MediaAttachmentList
